/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import {
  ListFleetDevicesResult,
  ListFleetDevicesResult$inboundSchema,
} from "../components/listfleetdevicesresult.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

export type ListFleetDevicesSecurity = {
  apikeyHeaderGramKey?: string | undefined;
  sessionHeaderGramSession?: string | undefined;
};

/**
 * Only devices in this coverage bucket.
 */
export const CoverageBucket = {
  AgentActive: "agent_active",
  AgentStale: "agent_stale",
  AgentOtherDevice: "agent_other_device",
  NoAgent: "no_agent",
  NoEmail: "no_email",
  UnresolvedEmail: "unresolved_email",
  Missing: "missing",
} as const;
/**
 * Only devices in this coverage bucket.
 */
export type CoverageBucket = ClosedEnum<typeof CoverageBucket>;

/**
 * Only agent_active devices in this policy state.
 */
export const PolicyState = {
  Attested: "attested",
  Outdated: "outdated",
  Unreported: "unreported",
  Halted: "halted",
} as const;
/**
 * Only agent_active devices in this policy state.
 */
export type PolicyState = ClosedEnum<typeof PolicyState>;

export type ListFleetDevicesRequest = {
  /**
   * Only devices synced from this provider. Excludes agent-only machines.
   */
  provider?: string | undefined;
  /**
   * true for devices a connected MDM holds, false for agent-only machines.
   */
  inInventory?: boolean | undefined;
  /**
   * Only devices in this coverage bucket.
   */
  coverageBucket?: CoverageBucket | undefined;
  /**
   * Only agent_active devices in this policy state.
   */
  policyState?: PolicyState | undefined;
  /**
   * Only devices of these Speakeasy users. Combined with user_emails as an OR.
   */
  userIds?: Array<string> | undefined;
  /**
   * Only devices whose user_email is one of these, matched case-insensitively.
   */
  userEmails?: Array<string> | undefined;
  /**
   * Pagination cursor from a previous page.
   */
  cursor?: string | undefined;
  /**
   * Page size. Defaults to 50, maximum 200.
   */
  limit?: number | undefined;
  /**
   * API Key header
   */
  gramKey?: string | undefined;
  /**
   * Session header
   */
  gramSession?: string | undefined;
};

export type ListFleetDevicesResponse = {
  result: ListFleetDevicesResult;
};

/** @internal */
export type ListFleetDevicesSecurity$Outbound = {
  "apikey_header_Gram-Key"?: string | undefined;
  "session_header_Gram-Session"?: string | undefined;
};

/** @internal */
export const ListFleetDevicesSecurity$outboundSchema: z.ZodMiniType<
  ListFleetDevicesSecurity$Outbound,
  ListFleetDevicesSecurity
> = z.pipe(
  z.object({
    apikeyHeaderGramKey: z.optional(z.string()),
    sessionHeaderGramSession: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      apikeyHeaderGramKey: "apikey_header_Gram-Key",
      sessionHeaderGramSession: "session_header_Gram-Session",
    });
  }),
);

export function listFleetDevicesSecurityToJSON(
  listFleetDevicesSecurity: ListFleetDevicesSecurity,
): string {
  return JSON.stringify(
    ListFleetDevicesSecurity$outboundSchema.parse(listFleetDevicesSecurity),
  );
}

/** @internal */
export const CoverageBucket$outboundSchema: z.ZodMiniEnum<
  typeof CoverageBucket
> = z.enum(CoverageBucket);

/** @internal */
export const PolicyState$outboundSchema: z.ZodMiniEnum<typeof PolicyState> = z
  .enum(PolicyState);

/** @internal */
export type ListFleetDevicesRequest$Outbound = {
  provider?: string | undefined;
  in_inventory?: boolean | undefined;
  coverage_bucket?: string | undefined;
  policy_state?: string | undefined;
  user_ids?: Array<string> | undefined;
  user_emails?: Array<string> | undefined;
  cursor?: string | undefined;
  limit: number;
  "Gram-Key"?: string | undefined;
  "Gram-Session"?: string | undefined;
};

/** @internal */
export const ListFleetDevicesRequest$outboundSchema: z.ZodMiniType<
  ListFleetDevicesRequest$Outbound,
  ListFleetDevicesRequest
> = z.pipe(
  z.object({
    provider: z.optional(z.string()),
    inInventory: z.optional(z.boolean()),
    coverageBucket: z.optional(CoverageBucket$outboundSchema),
    policyState: z.optional(PolicyState$outboundSchema),
    userIds: z.optional(z.array(z.string())),
    userEmails: z.optional(z.array(z.string())),
    cursor: z.optional(z.string()),
    limit: z._default(z.int(), 50),
    gramKey: z.optional(z.string()),
    gramSession: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      inInventory: "in_inventory",
      coverageBucket: "coverage_bucket",
      policyState: "policy_state",
      userIds: "user_ids",
      userEmails: "user_emails",
      gramKey: "Gram-Key",
      gramSession: "Gram-Session",
    });
  }),
);

export function listFleetDevicesRequestToJSON(
  listFleetDevicesRequest: ListFleetDevicesRequest,
): string {
  return JSON.stringify(
    ListFleetDevicesRequest$outboundSchema.parse(listFleetDevicesRequest),
  );
}

/** @internal */
export const ListFleetDevicesResponse$inboundSchema: z.ZodMiniType<
  ListFleetDevicesResponse,
  unknown
> = z.pipe(
  z.object({
    Result: ListFleetDevicesResult$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "Result": "result",
    });
  }),
);

export function listFleetDevicesResponseFromJSON(
  jsonString: string,
): SafeParseResult<ListFleetDevicesResponse, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListFleetDevicesResponse$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListFleetDevicesResponse' from JSON`,
  );
}
